import React, { useCallback } from "react";
import { Card, Space, Upload, Button, message, Select, Table, Input, InputNumber } from "antd";
import { UploadOutlined, DeleteOutlined, PaperClipOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { Attachment, EvaluationCriteria, SourcingType, VisibleToSuppliers, EvaluationCriteriaType, TechnicalEvaluationMode } from "../interface";

const { Option } = Select;

interface AttachmentsStepProps {
  attachments?: Attachment[];
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  type?: SourcingType;
  technicalEvaluationMode?: TechnicalEvaluationMode;
  evaluationCriterias?: EvaluationCriteria[];
  onEvaluationCriteriasChange?: (criterias: EvaluationCriteria[]) => void;
}

const deriveFileApiBase = () => {
  return  import.meta.env.VITE_API_URL
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;

const ATTACHMENT_CATEGORIES = [
  { value: 'TOR', label: 'Terms of Reference' },
  { value: 'SPECIFICATION', label: 'Technical Specification' },
  { value: 'CONTRACT', label: 'Contract Template' },
  { value: 'EVALUATION', label: 'Evaluation Criteria' },
  { value: 'QUOTE', label: 'Quotation Document' },
  { value: 'OTHER', label: 'Other' },
];

export const AttachmentsStep: React.FC<AttachmentsStepProps> = ({
  attachments = [],
  onAttachmentsChange,
  type,
  technicalEvaluationMode,
  evaluationCriterias = [],
  onEvaluationCriteriasChange
}) => {
  const getMimeTypeIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) {
      return <FilePdfOutlined />;
    } else if (mimeType.includes("image")) {
      return <FileImageOutlined />;
    } else if (mimeType.includes("text")) {
      return <FileTextOutlined />;
    }
    return <FileOutlined />;
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const handleRemoveAttachment = useCallback((fileIdToRemove: string) => {
    const next = attachments.filter((a: Attachment) => a.fileId !== fileIdToRemove);
    onAttachmentsChange?.(next);
    message.success("Attachment removed");
  }, [attachments, onAttachmentsChange]);

  const handleCategoryChange = useCallback((fileId: string, category: string) => {
    const next = attachments.map((a: Attachment) =>
      a.fileId === fileId ? { ...a, category } : a
    );
    onAttachmentsChange?.(next);
  }, [attachments, onAttachmentsChange]);

  const handleAddCriteria = useCallback(() => {
    const newCriteria: EvaluationCriteria = {
      id: `temp-${Date.now()}`,
      name: '',
      description: '',
      weight: 0,
      visibleToSuppliers: VisibleToSuppliers.FULL,
      threshold: undefined,
      type: EvaluationCriteriaType.TECHNICAL,
    };
    onEvaluationCriteriasChange?.([...evaluationCriterias, newCriteria]);
  }, [evaluationCriterias, onEvaluationCriteriasChange]);

  const handleRemoveCriteria = useCallback((id: string) => {
    const next = evaluationCriterias.filter(c => c.id !== id);
    onEvaluationCriteriasChange?.(next);
  }, [evaluationCriterias, onEvaluationCriteriasChange]);

  const handleCriteriaChange = useCallback((id: string, field: keyof EvaluationCriteria, value: any) => {
    const next = evaluationCriterias.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    );
    onEvaluationCriteriasChange?.(next);
  }, [evaluationCriterias, onEvaluationCriteriasChange]);

  const getCategoryLabel = (category: string) => {
    const categoryOption = ATTACHMENT_CATEGORIES.find(cat => cat.value === category);
    return categoryOption ? categoryOption.label : category;
  };

  const uploadProps = {
    name: "file",
    multiple: true,
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }
        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }
        // Create attachment data
        const attachmentData: Attachment = {
          fileId: fileData.fileId,
          originalName: fileData.originalName,
          mimeType: fileData.mimeType,
          size: fileData.size,
          url: fileData.url,
        };
        const next = [...attachments, attachmentData];
        onAttachmentsChange?.(next);
        onSuccess?.(json, file);
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
  };

  return (
    <>
    <Card
      title="Additional Informations"
      style={{ marginTop: 16 }}
      extra={
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>
            Upload Files
          </Button>
        </Upload>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ color: '#666', marginBottom: 8 }}>
          Upload supporting documents for your sourcing event:
        </p>
        <ul style={{ color: '#666', margin: 0, paddingLeft: 20 }}>
          <li>Terms of Reference (TOR)</li>
          <li>Technical Specifications</li>
          <li>Contract Templates</li>
          <li>Evaluation Criteria</li>
          <li>Other relevant documents</li>
        </ul>
      </div>

      <Space wrap>
        {attachments.map((att: Attachment) => (
          <div key={att.fileId} style={{ border: "1px solid #eee", padding: 12, borderRadius: 6, minWidth: 250 }}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Space>
                {getMimeTypeIcon(att.mimeType)}
                <a href={att.url} target="_blank" rel="noreferrer" style={{ fontWeight: 500 }}>
                  {att.originalName}
                </a>
              </Space>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {formatBytes(att.size)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Category:</strong>
              </div>
              <Select
                placeholder="Select category"
                value={att.category}
                onChange={(value) => handleCategoryChange(att.fileId, value)}
                style={{ width: "100%" }}
                size="small"
              >
                {ATTACHMENT_CATEGORIES.map(category => (
                  <Option key={category.value} value={category.value}>
                    {category.label}
                  </Option>
                ))}
              </Select>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveAttachment(att.fileId)}
                block
              >
                Remove
              </Button>
            </Space>
          </div>
        ))}
      </Space>

      {!attachments?.length && (
        <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
          <PaperClipOutlined style={{ fontSize: '24px', marginBottom: 8 }} />
          <div>No attachments uploaded yet</div>
          <div style={{ fontSize: '12px' }}>Click "Upload Files" to add documents</div>
        </div>
      )}
    </Card>

    {technicalEvaluationMode === TechnicalEvaluationMode.MATRIX && (
      <Card
        title="Evaluation Criteria"
        style={{ marginTop: 16 }}
        extra={
          <Button icon={<PlusOutlined />} onClick={handleAddCriteria}>
            Add Criteria
          </Button>
        }
      >
        {/* Technical Criteria Section */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 16, color: '#1890ff' }}>Technical Criteria</h4>
          <Table
            dataSource={evaluationCriterias.filter(c => c.type === EvaluationCriteriaType.TECHNICAL)}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                render: (value, record) => (
                  <Input
                    value={value}
                    onChange={(e) => handleCriteriaChange(record.id, 'name', e.target.value)}
                    placeholder="Criterion name"
                  />
                ),
              },
              {
                title: 'Description',
                dataIndex: 'description',
                render: (value, record) => (
                  <Input
                    value={value}
                    onChange={(e) => handleCriteriaChange(record.id, 'description', e.target.value)}
                    placeholder="Optional description"
                  />
                ),
              },
              {
                title: 'Weight (%)',
                dataIndex: 'weight',
                width: 120,
                render: (value, record) => (
                  <InputNumber
                    min={0}
                    max={100}
                    value={value}
                    onChange={(val) => handleCriteriaChange(record.id, 'weight', val)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Threshold',
                dataIndex: 'threshold',
                width: 120,
                render: (value, record) => (
                  <InputNumber
                    min={0}
                    value={value}
                    onChange={(val) => handleCriteriaChange(record.id, 'threshold', val)}
                    placeholder="Optional"
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Actions',
                width: 80,
                render: (_, record) => (
                  <Button
                    icon={<MinusOutlined />}
                    danger
                    onClick={() => handleRemoveCriteria(record.id)}
                  />
                ),
              },
            ]}
          />
          {evaluationCriterias.filter(c => c.type === EvaluationCriteriaType.TECHNICAL).length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', padding: '10px 0' }}>
              <div>No technical criteria added yet</div>
            </div>
          )}
        </div>

        {/* Financial Criteria Section */}
        <div>
          <h4 style={{ marginBottom: 16, color: '#52c41a' }}>Financial Criteria</h4>
          <Table
            dataSource={evaluationCriterias.filter(c => c.type === EvaluationCriteriaType.FINANCIAL)}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                render: (value, record) => (
                  <Input
                    value={value}
                    onChange={(e) => handleCriteriaChange(record.id, 'name', e.target.value)}
                    placeholder="Criterion name"
                  />
                ),
              },
              {
                title: 'Description',
                dataIndex: 'description',
                render: (value, record) => (
                  <Input
                    value={value}
                    onChange={(e) => handleCriteriaChange(record.id, 'description', e.target.value)}
                    placeholder="Optional description"
                  />
                ),
              },
              {
                title: 'Weight (%)',
                dataIndex: 'weight',
                width: 120,
                render: (value, record) => (
                  <InputNumber
                    min={0}
                    max={100}
                    value={value}
                    onChange={(val) => handleCriteriaChange(record.id, 'weight', val)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Threshold',
                dataIndex: 'threshold',
                width: 120,
                render: (value, record) => (
                  <InputNumber
                    min={0}
                    value={value}
                    onChange={(val) => handleCriteriaChange(record.id, 'threshold', val)}
                    placeholder="Optional"
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Actions',
                width: 80,
                render: (_, record) => (
                  <Button
                    icon={<MinusOutlined />}
                    danger
                    onClick={() => handleRemoveCriteria(record.id)}
                  />
                ),
              },
            ]}
          />
          {evaluationCriterias.filter(c => c.type === EvaluationCriteriaType.FINANCIAL).length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', padding: '10px 0' }}>
              <div>No financial criteria added yet</div>
            </div>
          )}
        </div>

        {!evaluationCriterias?.length && (
          <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
            <div>No evaluation criteria added yet</div>
            <div style={{ fontSize: '12px' }}>Click "Add Criteria" to define evaluation criteria</div>
          </div>
        )}
      </Card>
    )}
    </>
  );
};